import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, DollarSign, ClipboardCheck, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import heroImage from "@/assets/blog-personal-injury-claim.jpg";
import { generateArticleSchema } from "@/lib/seo-schemas";

const PersonalInjuryClaim = () => {
  const schema = generateArticleSchema(
    "How to Handle a Personal Injury Claim",
    "Step-by-step guide to maximizing your personal injury settlement after an accident.",
    "2024-12-15",
    "2024-12-15",
    "https://danielalbertlaw.com/blog/handle-personal-injury-claim"
  );

  return (
    <div className="min-h-screen">
      <SEO
        title="How to Handle a Personal Injury Claim | Daniel Albert Law"
        description="Step-by-step guide to maximizing your personal injury settlement after an accident. Experienced personal injury attorney advice for Houston residents."
        keywords="personal injury claim, accident settlement, injury lawyer Houston, car accident claim, compensation"
        schema={schema}
      />
      
      <Header />
      
      <Breadcrumbs
        items={[
          { label: "Blog", href: "/blog" },
          { label: "How to Handle a Personal Injury Claim" }
        ]}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl text-primary-foreground">
            <div className="flex items-center gap-4 mb-4 text-sm">
              <span className="inline-block bg-accent/20 text-accent px-3 py-1 rounded-full font-semibold">
                Personal Injury
              </span>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>December 15, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Daniel Albert</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">
              How to Handle a Personal Injury Claim
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Being injured in an accident—whether it's a car crash, slip and fall, or workplace incident—can be overwhelming. Beyond the physical pain and emotional trauma, you're faced with mounting medical bills, lost wages, and uncertainty about your future. Understanding how to properly handle your personal injury claim can make the difference between a fair settlement and being left to bear the financial burden alone.
              </p>
            </div>

            {/* Key Actions Card */}
            <Card className="mb-12 bg-gradient-subtle">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <ClipboardCheck className="h-6 w-6 text-accent" />
                  Critical First Steps
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Seek immediate medical attention—even if you feel fine</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Document everything: take photos, get witness information, keep all records</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Report the accident to relevant parties (police, property owner, employer)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Don't give recorded statements to insurance companies without an attorney</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Contact a personal injury attorney as soon as possible</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Main Content Sections */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-bold mb-6">Step 1: Seek Medical Treatment Immediately</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Your health is the top priority. Seek medical attention immediately after an accident, even if you don't think you're seriously injured. Some injuries, like whiplash, concussions, or internal bleeding, may not show symptoms right away.
                </p>
                <div className="bg-gradient-subtle p-6 rounded-lg mb-6">
                  <h3 className="text-xl font-semibold mb-3">Why Immediate Medical Care Matters:</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                      <span><strong>Health:</strong> Early detection and treatment prevent complications</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                      <span><strong>Documentation:</strong> Creates an official record linking your injuries to the accident</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                      <span><strong>Credibility:</strong> Delays in treatment can be used against you by insurance companies</span>
                    </li>
                  </ul>
                </div>
                <p className="text-lg text-muted-foreground">
                  Follow all medical advice, attend all appointments, and keep detailed records of all treatments, prescriptions, and medical expenses.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Step 2: Document Everything</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Strong documentation is the foundation of a successful personal injury claim. The more evidence you have, the stronger your case.
                </p>
                <div className="space-y-6">
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">At the Scene</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Take photos and videos of the accident scene, your injuries, vehicle damage, hazards, weather conditions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Get contact information from all parties involved and witnesses</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Note the time, date, location, and circumstances of the accident</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Obtain a copy of the police report if applicable</span>
                      </li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">Ongoing Documentation</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Keep all medical records, bills, and receipts</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Document lost wages with pay stubs and employer letters</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Maintain a daily journal of pain levels, limitations, and how injuries affect your life</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Save all correspondence with insurance companies</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Step 3: Report the Accident</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Depending on the type of accident, you need to report it to the appropriate parties:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>Car Accidents:</strong> Call police immediately and file a report. In Texas, you must report accidents involving injury, death, or property damage over $1,000.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>Slip and Falls:</strong> Notify the property owner or manager and request an incident report.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>Workplace Injuries:</strong> Report to your employer immediately—you typically have 30 days to report for workers' compensation.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>Your Insurance:</strong> Notify your own insurance company, but provide only basic facts—do not discuss fault or give detailed statements without an attorney.</span>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Step 4: Be Careful When Dealing with Insurance Companies</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Insurance companies are businesses focused on minimizing payouts. Here's how to protect yourself:
                </p>
                <div className="bg-gradient-subtle p-6 rounded-lg mb-6">
                  <h3 className="text-xl font-semibold mb-4 text-destructive">What NOT to Do:</h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-3">
                      <span className="text-destructive">✗</span>
                      <span>Don't give a recorded statement without consulting an attorney</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-destructive">✗</span>
                      <span>Don't admit fault or apologize—even casual statements can be used against you</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-destructive">✗</span>
                      <span>Don't accept the first settlement offer—initial offers are almost always too low</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-destructive">✗</span>
                      <span>Don't sign any releases or waivers without attorney review</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-destructive">✗</span>
                      <span>Don't post about your accident or injuries on social media</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Step 5: Calculate Your Damages</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Your personal injury claim should cover all damages, both economic and non-economic:
                </p>
                <div className="space-y-6">
                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                      <DollarSign className="h-6 w-6 text-accent" />
                      Economic Damages
                    </h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Medical expenses (past and future)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Lost wages and loss of earning capacity</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Property damage</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Rehabilitation and therapy costs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Home modifications if permanently disabled</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-4">Non-Economic Damages</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Pain and suffering</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Emotional distress and mental anguish</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Loss of enjoyment of life</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Disfigurement and scarring</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Loss of consortium (impact on relationships)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Step 6: Hire a Personal Injury Attorney</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  The single most important decision you can make is to hire an experienced personal injury attorney. Here's why:
                </p>
                <ul className="space-y-4 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Studies show injured people with attorneys receive 3.5 times more compensation</strong> than those without representation.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <div>
                      Attorneys handle all communication with insurance companies, protecting you from their tactics.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <div>
                      They accurately value your claim, ensuring you don't settle for less than you deserve.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <div>
                      Most work on contingency—you pay nothing unless you win your case.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <div>
                      They're prepared to take your case to trial if necessary for fair compensation.
                    </div>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Texas Statute of Limitations</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  In Texas, you generally have <strong>two years from the date of injury</strong> to file a personal injury lawsuit. There are exceptions, but waiting too long can result in losing your right to compensation entirely. Don't wait—contact an attorney as soon as possible after your accident.
                </p>
              </section>
            </div>

            {/* Related Services */}
            <Card className="mt-12 bg-primary text-primary-foreground">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4">Injured in an Accident? We Fight for Maximum Compensation</h2>
                <p className="text-lg mb-6 opacity-95">
                  Daniel Albert Law Firm has recovered millions of dollars for injured clients. We handle all types of personal injury cases including car accidents, truck accidents, slip and falls, and more. We work on contingency—no fee unless we win.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="outline" className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    <Link to="/practice-areas/personal-injury">
                      Learn More About Personal Injury Services
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <CTASection
        title="Don't Settle for Less Than You Deserve"
        description="Contact us today. We'll fight to get you the compensation you need to recover and move forward."
      />

      <Footer />
    </div>
  );
};

export default PersonalInjuryClaim;